<template>
  <div class="dashboard">
    <!-- 欢迎横幅 -->
    <el-card class="hero-card" shadow="never">
      <div class="hero">
        <div class="hero-text">
          <h2>{{ greeting }}，{{ userName }}</h2>
          <p>{{ todayText }} · 欢迎使用</p>
        </div>
        <!-- 机构管理员：服务到期时间（放入欢迎横幅） -->
        <div v-if="isCompany" class="hero-expire">
          <i class="el-icon-alarm-clock hero-expire-icon" :style="{ color: expireColor }"></i>
          <div class="hero-expire-info">
            <div class="hero-expire-date">服务到期 {{ expireDisplayText }}</div>
            <div class="hero-expire-sub" :style="{ color: expireColor }">{{ expireSubText }}</div>
          </div>
        </div>
        <div v-else class="hero-badge">
          <i class="el-icon-data-line"></i>
        </div>
      </div>
    </el-card>

    <!-- 快捷操作 -->
    <el-card class="quick-card" shadow="never">
      <div class="quick-title">快捷操作</div>
      <div class="quick-list">
        <div v-for="q in quickActions" :key="q.url" class="quick-item" @click="goPage(q.url)">
          <i class="quick-icon" :class="'el-icon-' + q.icon" :style="{ color: q.color, background: q.bg }"></i>
          <span class="quick-name">{{ q.label }}</span>
        </div>
      </div>
    </el-card>

    <!-- 统计卡片 -->
    <div class="stat-grid">
      <el-card v-for="card in statCards" :key="card.key" class="stat-card" shadow="never">
        <div class="stat-inner">
          <div class="stat-icon" :class="'tone-' + card.tone">
            <i :class="'el-icon-' + card.icon"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ fmtNum(overview[card.key], card.isMoney) }}</div>
            <div class="stat-label">{{ card.label }}</div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 图表区 -->
    <div class="chart-grid" :class="{ 'chart-grid-promo': isPromo }">
      <el-card class="chart-card" shadow="never">
        <div slot="header" class="chart-head">
          <span class="chart-title">入量趋势</span>
          <span class="chart-sub">近 30 天 · 线索录入 / 成交</span>
        </div>
        <div ref="trendChart" class="chart-box"></div>
      </el-card>
      <el-card v-if="!isPromo" class="chart-card" shadow="never">
        <div slot="header" class="chart-head">
          <span class="chart-title">业绩排行</span>
          <span class="chart-sub">本月核算业绩 Top 8</span>
        </div>
        <div ref="rankChart" class="chart-box"></div>
      </el-card>
      <el-card class="chart-card" shadow="never">
        <div slot="header" class="chart-head">
          <span class="chart-title">线索来源</span>
          <span class="chart-sub">近 30 天 · 渠道占比</span>
        </div>
        <div ref="distChart" class="chart-box"></div>
      </el-card>
    </div>

    <!-- 近期动态 -->
    <el-card v-if="!isPromo" class="chart-card" shadow="never">
      <div slot="header" class="chart-head">
        <span class="chart-title">近期动态</span>
      </div>
      <div class="recent-grid">
        <div class="recent-col">
          <div class="recent-head">最新线索</div>
          <div v-for="l in recentLeads" :key="l.id" class="recent-item">
            <span class="recent-dot tone-blue"></span>
            <span class="recent-text">{{ l.liang_account || '—' }}</span>
            <span class="recent-state">{{ recentLeadStatus(l.status) }}</span>
          </div>
          <div v-if="recentLeads.length === 0" class="recent-empty">暂无线索</div>
        </div>
        <div class="recent-col">
          <div class="recent-head">最新订单</div>
          <div v-for="o in recentOrders" :key="o.id" class="recent-item">
            <span class="recent-dot tone-green"></span>
            <span class="recent-text">{{ o.customer_name || '—' }}</span>
            <span class="recent-price">¥ {{ fmtNum(o.price, true) }}</span>
          </div>
          <div v-if="recentOrders.length === 0" class="recent-empty">暂无订单</div>
        </div>
      </div>
    </el-card>

    <!-- 待办提醒 -->
    <el-card v-if="!isPromo" class="todo-card" shadow="never">
      <div slot="header" class="chart-head">
        <span class="chart-title">待办提醒</span>
      </div>
      <div class="todo-list">
        <div v-for="t in todos" :key="t.key" class="todo-item" @click="goPage(t.url)">
          <span class="todo-dot" :class="'tone-' + t.tone"></span>
          <span class="todo-label">{{ t.label }}</span>
          <span class="todo-count">{{ overview[t.key] || 0 }}</span>
          <i class="el-icon-arrow-right todo-arrow"></i>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapState } from 'vuex'
import request from '@/utils/request'
import { companyInfo } from '@/api/company'

export default {
  name: 'Dashboard',
  data() {
    return {
      overview: {},
      companyInfo: null,
      trendChart: null,
      rankChart: null,
      distChart: null,
      recentLeads: [],
      recentOrders: []
    }
  },
  computed: {
    ...mapState(['user']),
    isPromo() {
      if (!this.user) return false
      if (this.user.type === 'company') return false
      // 推广员工（staff+is_promo），或归属于推广战队（unit_type=1）的推广组长/战队长
      if (this.user.type === 'staff' && parseInt(this.user.is_promo) === 1) return true
      return parseInt(this.user.unit_type) === 1
    },
    isCompany() {
      return !!(this.user && this.user.type === 'company')
    },
    /* 服务到期天数（来自公司信息；0 或负视为已到期） */
    expireDays() {
      return this.companyInfo && this.companyInfo.expire_days !== null && this.companyInfo.expire_days !== undefined ? this.companyInfo.expire_days : null
    },
    expireText() {
      if (this.expireDays === null) return '—'
      return this.companyInfo.expire_date || '—'
    },
    expireDisplayText() {
      return this.expireDays === null ? '长期有效' : (this.companyInfo.expire_date || '长期有效')
    },
    expireSubText() {
      if (this.expireDays === null) return ''
      if (this.expireDays <= 0) return '已到期，请及时续费'
      return '剩余 ' + this.expireDays + ' 天'
    },
    expireColor() {
      if (this.expireDays === null) return '#7FD1FF'
      if (this.expireDays <= 0) return '#FF6B6B'
      if (this.expireDays <= 30) return '#FFB25C'
      return '#7FD1FF'
    },
    quickActions() {
      // 推广人员：只保留线索录入相关快捷操作
      if (this.isPromo) {
        return [
          { label: '新增线索', icon: 'user', url: '/lead/list', color: '#34A46F', bg: '#E8F7EE' }
        ]
      }
      return [
        { label: '新建订单', icon: 'circle-plus-outline', url: '/order/add', color: '#3668FC', bg: '#EDF3FE' },
        { label: '新增线索', icon: 'user', url: '/lead/list', color: '#34A46F', bg: '#E8F7EE' },
        { label: '生成协议', icon: 'document-copy', url: '/agreement/list', color: '#E8833A', bg: '#FEF2E7' },
        { label: '账单登记', icon: 'money', url: '/bill/list', color: '#7B61E6', bg: '#F1EEFD' },
        { label: '分期办理', icon: 'coin', url: '/installment/list', color: '#0FA3A3', bg: '#E6F7F7' },
        { label: 'Excel 导入', icon: 'upload2', url: '/tools/import', color: '#E5484D', bg: '#FEEBEC' }
      ]
    },
    userName() {
      return (this.user && this.user.name) || '朋友'
    },
    greeting() {
      const h = new Date().getHours()
      if (h < 6) return '夜深了'
      if (h < 9) return '早上好'
      if (h < 12) return '上午好'
      if (h < 14) return '中午好'
      if (h < 18) return '下午好'
      return '晚上好'
    },
    todayText() {
      const d = new Date()
      const week = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()]
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 星期${week}`
    },
    statCards() {
      // 推广人员：自己的录入/成交统计
      if (this.isPromo) {
        return [
          { key: 'today_leads', label: '今日录入线索', icon: 'search', tone: 'blue', isMoney: false },
          { key: 'total_leads', label: '累计录入线索', icon: 'data-analysis', tone: 'green', isMoney: false },
          { key: 'today_deals', label: '今日成交', icon: 'finished', tone: 'orange', isMoney: false },
          { key: 'total_deals', label: '累计成交', icon: 'circle-check', tone: 'violet', isMoney: false }
        ]
      }
      return [
        { key: 'today_leads', label: '今日新增线索', icon: 'search', tone: 'blue', isMoney: false },
        { key: 'total_leads', label: '累计线索', icon: 'data-analysis', tone: 'green', isMoney: false },
        { key: 'month_deals', label: '本月成交', icon: 'finished', tone: 'violet', isMoney: false },
        { key: 'month_final', label: '本月净业绩(元)', icon: 'data-line', tone: 'blue', isMoney: true },
        { key: 'pending_bills', label: '待审核账单', icon: 'document', tone: 'orange', isMoney: false },
        { key: 'pending_refunds', label: '待审核退款', icon: 'refresh-left', tone: 'red', isMoney: false },
        { key: 'overdue_bills', label: '逾期分期数', icon: 'time', tone: 'violet', isMoney: false }
      ]
    },
    todos() {
      if (this.isPromo) return []
      return [
        { key: 'pending_bills', label: '待审核账单', tone: 'orange', url: '/bill/list' },
        { key: 'pending_refunds', label: '待审核退款', tone: 'red', url: '/refund/list' },
        { key: 'overdue_bills', label: '逾期分期跟进', tone: 'violet', url: '/installment/list' }
      ]
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.trendChart) { this.trendChart.dispose(); this.trendChart = null }
    if (this.rankChart) { this.rankChart.dispose(); this.rankChart = null }
    if (this.distChart) { this.distChart.dispose(); this.distChart = null }
  },
  methods: {
    async loadData() {
      const end = new Date()
      const start = new Date()
      start.setDate(end.getDate() - 29)
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const startDate = fmt(start)
      const endDate = fmt(end)
      const monthStart = fmt(new Date(end.getFullYear(), end.getMonth(), 1))

      // 并行接口（后端 Stats 用 GET 读参）+ 机构到期信息
      const [ov, trend, perform, dist, recent, cv] = await Promise.all([
        request.get('/stats/overview').catch(() => ({})),
        request.get('/stats/leadTrend', { params: { start_date: startDate, end_date: endDate } }).catch(() => ({ list: [] })),
        request.get('/stats/perform', { params: { level: 'staff', start_date: monthStart, end_date: endDate } }).catch(() => ({ list: [] })),
        request.get('/stats/channelDist').catch(() => ({ list: [] })),
        request.get('/stats/recent').catch(() => ({ leads: [], orders: [] })),
        this.isCompany ? companyInfo().catch(() => null) : Promise.resolve(null)
      ])
      this.overview = ov || {}
      this.companyInfo = cv || null
      this.recentLeads = (recent && recent.leads) || []
      this.recentOrders = (recent && recent.orders) || []
      this.$nextTick(() => {
        this.renderTrend(trend || { list: [] })
        this.renderRank(perform || { list: [] })
        this.renderDist(dist || { list: [] })
      })
    },
    /* 入量趋势折线图 */
    renderTrend(data) {
      const el = this.$refs.trendChart
      if (!el) return
      if (!el.offsetWidth || !el.offsetHeight) {
        setTimeout(() => this.renderTrend(data), 100)
        return
      }
      if (!this.trendChart) this.trendChart = echarts.init(el)
      const days = (data.list || []).map(i => String(i.day).slice(5))
      const totals = (data.list || []).map(i => i.total)
      const dealt = (data.list || []).map(i => i.dealt)
      this.trendChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['录入', '成交'], top: 0, right: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: { color: '#8A94AD', fontSize: 12 } },
        grid: { left: 10, right: 10, top: 34, bottom: 0, containLabel: true },
        xAxis: {
          type: 'category',
          data: days,
          axisLine: { lineStyle: { color: '#E3E8F4' } },
          axisTick: { show: false },
          axisLabel: { color: '#8A94AD', fontSize: 11, interval: Math.max(0, Math.floor(days.length / 8) - 1) }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: { lineStyle: { color: '#F0F2F6' } },
          axisLabel: { color: '#8A94AD', fontSize: 11 }
        },
        series: [
          { name: '录入', type: 'line', smooth: true, data: totals, symbol: 'none', lineStyle: { width: 2, color: '#3668FC' }, areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(54,104,252,0.18)' }, { offset: 1, color: 'rgba(54,104,252,0)' }] } } },
          { name: '成交', type: 'line', smooth: true, data: dealt, symbol: 'none', lineStyle: { width: 2, color: '#5B8DFF' } }
        ]
      })
    },
    /* 业绩排行条形图（横向） */
    renderRank(data) {
      const el = this.$refs.rankChart
      if (!el) return
      const list = (data.list || []).slice(0, 8).reverse()
      // 无数据：显示空态占位，不初始化 echarts（规避空 category 渲染异常）
      if (list.length === 0) {
        if (this.rankChart) { this.rankChart.dispose(); this.rankChart = null }
        el.innerHTML = ''
        const div = document.createElement('div')
        div.className = 'chart-empty'
        div.textContent = '暂无业绩数据'
        el.appendChild(div)
        return
      }
      if (!el.offsetWidth || !el.offsetHeight) {
        setTimeout(() => this.renderRank(data), 100)
        return
      }
      if (!this.rankChart) this.rankChart = echarts.init(el)
      const names = list.map(i => i.name)
      const values = list.map(i => i.final_amount)
      this.rankChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: p => `${p[0].name}<br/>业绩：${Number(p[0].value).toLocaleString()} 元` },
        grid: { left: 10, right: 30, top: 10, bottom: 0, containLabel: true },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#F0F2F6' } }, axisLabel: { color: '#8A94AD', fontSize: 11, formatter: v => v >= 10000 ? (v / 10000).toFixed(1) + 'w' : v } },
        yAxis: { type: 'category', data: names, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#5A6580', fontSize: 12 } },
        series: [{
          type: 'bar',
          data: values,
          barWidth: 12,
          itemStyle: { borderRadius: [0, 6, 6, 0], color: { type: 'linear', x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: '#3668FC' }, { offset: 1, color: '#5B8DFF' }] } },
          label: { show: true, position: 'right', color: '#8A94AD', fontSize: 11, formatter: p => Number(p.value).toLocaleString() }
        }]
      })
      // 空数据也强制重绘一次，确保渲染层生成
      this.rankChart.resize()
    },
    handleResize() {
      if (this.trendChart) this.trendChart.resize()
      if (this.rankChart) this.rankChart.resize()
      if (this.distChart) this.distChart.resize()
    },
    /* 线索来源分布饼图 */
    renderDist(data) {
      const el = this.$refs.distChart
      if (!el) return
      if (!el.offsetWidth || !el.offsetHeight) {
        setTimeout(() => this.renderDist(data), 100)
        return
      }
      if (!this.distChart) this.distChart = echarts.init(el)
      const list = (data.list || []).map(i => ({ name: i.name, value: i.value }))
      this.distChart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} 条 ({d}%)' },
        legend: { bottom: 0, type: 'scroll', icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: { color: '#8A94AD', fontSize: 12 } },
        series: [{
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '44%'],
          data: list,
          label: { show: false },
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 }
        }]
      })
    },
    recentLeadStatus(s) {
      return ['', '待添加', '已添加', '已成交'][parseInt(s)] || ''
    },
    fmtNum(v, isMoney) {
      if (v === undefined || v === null || v === '') return '—'
      const n = Number(v)
      if (isNaN(n)) return v
      return isMoney ? n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n.toLocaleString('zh-CN')
    },
    goPage(url) {
      this.$router.push(url)
    }
  }
}
</script>

<style scoped>
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ===== 欢迎横幅 ===== */
.hero-card {
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #1B2A4D 0%, #24418A 55%, #3668FC 130%);
}

.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
}

.hero-text h2 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 1px;
}

.hero-text p {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

.hero-badge {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
}

.hero-expire {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.12);
  padding: 12px 20px;
  border-radius: 14px;
}
.hero-expire-icon { font-size: 26px; }
.hero-expire-date { font-size: 14px; font-weight: 700; color: #fff; letter-spacing: .5px; }
.hero-expire-sub { font-size: 12px; margin-top: 3px; }

/* ===== 统计卡片 ===== */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.stat-card {
  border-radius: 12px;
  border: none;
}

.stat-inner {
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.tone-blue { background: #EDF3FE; color: #3668FC; }
.tone-green { background: #E8F7EE; color: #34A46F; }
.tone-orange { background: #FEF2E7; color: #E8833A; }
.tone-red { background: #FEEBEC; color: #E5484D; }
.tone-violet { background: #F1EEFD; color: #7B61E6; }

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #18233C;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #8A94AD;
  margin-top: 4px;
}

/* ===== 图表区 ===== */
.chart-grid {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
}

/* 推广人员控制台：隐藏销量排行后图表单列铺满 */
.chart-grid-promo {
  grid-template-columns: 1fr;
}

.chart-card {
  border-radius: 12px;
  border: none;
}

.chart-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #18233C;
}

.chart-sub {
  font-size: 12px;
  color: #A8B2C9;
}

.chart-box {
  height: 320px;
}

.chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B4BDCF;
  font-size: 14px;
}

/* ===== 快捷操作 ===== */
.quick-card { border-radius: 12px; border: none; }
.quick-title { font-size: 13px; font-weight: 600; color: #8A94AD; margin-bottom: 12px; }
.quick-list { display: flex; flex-wrap: wrap; gap: 12px; }
.quick-item {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 14px; border: 1px solid #EDF0F7; border-radius: 10px;
  cursor: pointer; transition: all .2s; background: #fff;
}
.quick-item:hover { border-color: #3668FC; box-shadow: 0 4px 14px rgba(54,104,252,.08); }
.quick-icon { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
.quick-name { font-size: 13px; color: #18233C; }

/* ===== 近期动态 ===== */
.recent-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.recent-head { font-size: 13px; font-weight: 600; color: #8A94AD; margin-bottom: 10px; }
.recent-item { display: flex; align-items: center; gap: 8px; padding: 7px 0; }
.recent-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.recent-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; color: #18233C; }
.recent-state { font-size: 12px; color: #8A94AD; }
.recent-price { font-size: 12px; color: #3668FC; font-weight: 600; }
.recent-empty { color: #B4BDCF; font-size: 12px; padding: 10px 0; }

@media (max-width: 900px) {
  .recent-grid { grid-template-columns: 1fr; }
}

/* ===== 待办提醒 ===== */
.todo-card {
  border-radius: 12px;
  border: none;
}

.todo-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #F8FAFF;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.todo-item:hover {
  background: #F0F4FF;
  box-shadow: 0 2px 8px rgba(54, 104, 252, 0.08);
}

.todo-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.todo-label {
  font-size: 13px;
  color: #5A6580;
  flex: 1;
}

.todo-count {
  font-size: 15px;
  font-weight: 700;
  color: #18233C;
}

.todo-arrow {
  font-size: 13px;
  color: #C3CADB;
}

/* ===== 响应式 ===== */
@media (max-width: 1280px) {
  .stat-grid { grid-template-columns: repeat(3, 1fr); }
  .stat-grid-company { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 992px) {
  .stat-grid { grid-template-columns: repeat(2, 1fr); }
  .chart-grid { grid-template-columns: 1fr; }
  .todo-list { grid-template-columns: 1fr; }
}
</style>
